import '@/lib/muiLicense';
import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, CssBaseline, Box, Typography, Button } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { theme } from './ui/theme'
import { ThemeProvider as CustomThemeProvider } from './contexts/ThemeContext'
import './globals.css'
import { AuthProvider, useAuth } from '@/auth/AuthProvider'
import { useCanReadReference } from '@/auth/permissions'
import { AuthInitializer } from '@/ui/auth/AuthInitializer'
import { HoneypotGotchaModal } from '@/security/HoneypotGotchaModal'
import { KmlDialogProvider } from '@/contexts/KmlDialogContext'
import { PolygonSyncDialogProvider } from '@/contexts/PolygonSyncDialogContext'
import RouteProgressBar from '@/ui/shared/RouteProgressBar'

// Auto-reload on stale chunk errors after a new deployment.
// Firebase Hosting returns index.html (text/html) for missing JS chunks,
// causing MIME type errors. One reload fetches the fresh HTML + chunk manifest.
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

// Route-level code splitting — each route loads its own chunk on demand
const UISandboxPage = lazy(() => import('@/ui/demo/UISandboxPage'))
const ChatPanel = lazy(() => import('@/ui/chat/ChatPanel'))
const LandingPage = lazy(() => import('@/pages/LandingPage'))
const ContactPage = lazy(() => import('@/pages/ContactPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const DesignPortal = lazy(() => import('@/ui/design/DesignPortal'))
const ToolsPage = lazy(() => import('@/pages/tools/ToolsPage'))
const KmlToShapefilePage = lazy(() => import('@/pages/tools/KmlToShapefilePage'))
const SharedPortalPage = lazy(() => import('@/pages/SharedPortalPage'))
const PermitPortal = lazy(() => import('@/ui/permits/PermitPortal'))
const FieldApp = lazy(() => import('@/pages/field/FieldApp'))
const VaultPortal = lazy(() => import('@/ui/vault/VaultPortal'))
const ReferencePortal = lazy(() => import('@/ui/reference/ReferencePortal'))
const ConstructionPortal = lazy(() => import('@/ui/construction/ConstructionPortal'))
// FLEETMGR.2 — standalone full-screen Fleet Manager portal (Q-PLACEMENT=A).
const FleetManagerPortal = lazy(() => import('@/ui/fleet-manager/FleetManagerPortal'))
const AuthAction = lazy(() => import('@/pages/AuthAction'))
const MarketIntelPage = lazy(() => import('@/ui/market-intel/MarketIntelPage'))
// MA.12 T.X — global Data Sources page per FCC ToS C.1 attribution (DL.9 = C).
const DataSourcesPage = lazy(() => import('@/ui/market-intel/DataSourcesPage'))
// DD.MAPENGINE.0 — throwaway decision-spike harness. Dev-only route, never linked.
const MapEngineSpike = lazy(() => import('@/dev/MapEngineSpike'))

// Error boundary for /field so a crash doesn't blank the whole app
// (Legacy try/catch wrapper FieldAppSafe replaced by FieldErrorBoundary below.)
import { Component, type ReactNode, type ErrorInfo } from 'react';
type FieldErrorState = { error: Error | null; componentStack: string | null };
class FieldErrorBoundary extends Component<{ children: ReactNode }, FieldErrorState> {
  state: FieldErrorState = { error: null, componentStack: null };
  static getDerivedStateFromError(error: Error) { return { error, componentStack: null }; }
  componentDidCatch(err: Error, info: ErrorInfo) {
    // Surface to console for desktop devtools, AND keep the componentStack
    // so we can show it in the UI (iOS Safari Web Inspector requires a
    // tethered Mac — most field operators don't have that path).
    console.error('FieldApp crash:', err, info);
    this.setState({ componentStack: info.componentStack ?? null });
  }
  render() {
    const { error, componentStack } = this.state;
    if (error) {
      // Trim stacks aggressively — iOS screenshots have limited vertical
      // space, the first ~5 frames are almost always enough to identify
      // the throwing component.
      const stackLines = (error.stack || '').split('\n').slice(0, 8).join('\n');
      const componentLines = (componentStack || '').trim().split('\n').slice(0, 4).join('\n');
      // Browser context — needed to identify iOS Safari version-specific
      // bugs. Build sourcemaps are now enabled (vite.config.ts), so the
      // stack frames above resolve to real file:line via the .map files
      // when DevTools or our own resolver is available; for plain field
      // operators on iPhones, these context fields are still useful.
      const ctx = (typeof navigator !== 'undefined' && typeof window !== 'undefined')
        ? `${navigator.userAgent}\n  viewport=${window.innerWidth}x${window.innerHeight}  pixelRatio=${window.devicePixelRatio}  online=${navigator.onLine}`
        : 'navigator unavailable';
      const detail = `${error.name}: ${error.message}\n\n${stackLines}${componentStack ? '\n\n--- component stack ---\n' + componentLines : ''}\n\n--- browser ---\n${ctx}`;
      const copyDetail = () => {
        try {
          navigator.clipboard?.writeText(detail);
        } catch { /* clipboard not available — UI still shows the text */ }
      };
      return (
        <Box display="flex" flexDirection="column" alignItems="stretch" justifyContent="flex-start"
          minHeight="100vh" bgcolor="#0f172a" color="#f8fafc" gap={2} p={3}>
          <Box textAlign="center" pt={2}>
            <Typography variant="h6">Something went wrong</Typography>
            <Typography color="#94a3b8" fontSize="0.9rem">The field app encountered an error.</Typography>
          </Box>
          <Box
            component="pre"
            sx={{
              bgcolor: '#0a1424', color: '#fecaca',
              border: '1px solid #1e293b', borderRadius: 1,
              p: 1.5, fontSize: '0.7rem', lineHeight: 1.35,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              overflowX: 'auto', maxHeight: '50vh', overflowY: 'auto',
              m: 0,
            }}
          >
            {detail}
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} pb={2}>
            <Button variant="outlined" size="small" onClick={copyDetail}
              sx={{ color: '#94a3b8', borderColor: '#334155' }}>
              Copy error details
            </Button>
            <Button variant="outlined" onClick={() => { window.location.href = '/app'; }}
              sx={{ color: '#67e8f9', borderColor: '#67e8f9' }}>
              Open Desktop App
            </Button>
            <Button variant="text" onClick={() => {
              this.setState({ error: null, componentStack: null });
              window.location.reload();
            }} sx={{ color: '#94a3b8' }}>
              Try Again
            </Button>
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Re-wrap FieldApp with error boundary
const FieldAppWrapped = () => (
  <FieldErrorBoundary><FieldApp /></FieldErrorBoundary>
);

const IS_DEV = import.meta.env.VITE_API_URL?.includes('backend-dev')
if (IS_DEV) document.title = '[Dev] ' + document.title

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
})

if (import.meta.env.DEV) {
  ;(window as any).__queryClient = queryClient
}

// One theme carries both colour schemes; ThemeContext's `data-theme` attribute
// on <html> selects which set of CSS custom properties applies.
function MUIThemeWrapper({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  );
}

// Phase UX1: all `Require*` gates are pure ACL components — no spinner UI.
// While auth is resolving (!ready), we render `null`; the cold-boot splash
// (#ds-splash) stays visible until AuthProvider flips `ready=true`, and the
// RouteProgressBar provides animated continuity during warm-navigation gaps.

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireDesigner({ children }: { children: React.ReactNode }) {
  const { user, ready, role } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  // T.4 dead-role-check cleanup: 'designer' was never in AppRole — comparison
  // was always-true on the LHS, making the gate effectively admin+developer.
  // Behavior preserved.
  if (role !== 'admin' && role !== 'developer') {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}

// RequirePermitAccess — mirrors backend PERMIT_ROLES
const PERMIT_ROLES = new Set([
  'admin', 'developer', 'lead', 'permitting',
  'permit_lead', 'permit_staff',
  'pole_permit_lead', 'pole_permit_staff',
  'other_permit_lead', 'other_permit_staff',
]);

function RequirePermitAccess({ children }: { children: React.ReactNode }) {
  const { user, ready, role, permitRole } = useAuth();
  // Phase UX1 DL.8: single `ready` signal replaces the legacy
  // `loading || (user && !roleVerified)` double-gate that flashed two spinners.
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!PERMIT_ROLES.has(role) && !permitRole) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function RequireSecurityAdmin({ children }: { children: React.ReactNode }) {
  const { user, ready, isSecurityAdmin } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!isSecurityAdmin) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function RequireReferenceAccess({ children }: { children: React.ReactNode }) {
  const { user, ready } = useAuth();
  const canRead = useCanReadReference();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!canRead) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

// RoleBasedRedirect - all authenticated users see /app.
// T.4 dead-role-check cleanup: 'designer' was never in AppRole — the prior
// `role === 'designer'` comparison was always-false (TS2367), so the redirect
// to /design never fired. Wrapper kept as a passthrough in case future role-
// based routing branches off /app.
function RoleBasedRedirect({ children }: { children: React.ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <CustomThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AuthInitializer>
            <MUIThemeWrapper>
              {/* SEC.3: render at the top-level so the Gotcha overlay
                  appears above every other DeepSync surface when a
                  honeypot decoy is hit. */}
              <HoneypotGotchaModal />
              <BrowserRouter>
                <RouteProgressBar />
                <KmlDialogProvider>
                <PolygonSyncDialogProvider>
                {/* Phase UX1 T.3: Suspense fallback is intentionally null. The
                    RouteProgressBar mounted above provides the only loading
                    signal during lazy-chunk downloads, and the CssBaseline
                    theme background fills the brief render window without
                    flashing the previous content's mark/spinner. */}
                <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/app" element={<RoleBasedRedirect><UISandboxPage /></RoleBasedRedirect>} />
                  <Route path="/design" element={<RequireDesigner><DesignPortal /></RequireDesigner>} />
                  <Route path="/tools" element={<RequireAuth><ToolsPage /></RequireAuth>} />
                  <Route path="/tools/kml-to-shapefile" element={<RequireAuth><KmlToShapefilePage /></RequireAuth>} />
                  {/* Legacy aliases for /app — same component, same gate. Nothing
                      links here; kept only so old bookmarks don't 404. */}
                  <Route path="/ui-sandbox" element={<RoleBasedRedirect><UISandboxPage /></RoleBasedRedirect>} />
                  <Route path="/sandbox" element={<RoleBasedRedirect><UISandboxPage /></RoleBasedRedirect>} />
                  <Route path="/portal/:token" element={<SharedPortalPage />} />
                  <Route path="/permits" element={<RequirePermitAccess><PermitPortal /></RequirePermitAccess>} />
                  <Route path="/field" element={<RequireAuth><FieldAppWrapped /></RequireAuth>} />
                  <Route path="/vault" element={<RequireSecurityAdmin><VaultPortal /></RequireSecurityAdmin>} />
                  <Route path="/reference" element={<RequireReferenceAccess><ReferencePortal /></RequireReferenceAccess>} />
                  <Route path="/construction" element={<RequireAuth><ConstructionPortal /></RequireAuth>} />
                  {/* FLEETMGR.2 — top-level Fleet Manager portal. Route is open to any
                      authenticated user; the roster is scoped server-side
                      (resolve_readable_scopes), so a regular user just sees a smaller
                      fleet. The server gates the data, not the client. */}
                  <Route path="/fleet-manager" element={<RequireAuth><FleetManagerPortal /></RequireAuth>} />
                  <Route path="/market-intel" element={<RequireAuth><MarketIntelPage /></RequireAuth>} />
                  <Route path="/market-intel/data-sources" element={<RequireAuth><DataSourcesPage /></RequireAuth>} />
                  <Route path="/contractor" element={<RequireAuth><Navigate to="/app" replace /></RequireAuth>} />
                  {/* Phase EU.2 — Firebase Auth action handler (password reset, verify email).
                      Public route: users arrive here BEFORE having credentials. */}
                  <Route path="/auth/action" element={<AuthAction />} />
                  {/* DD.MAPENGINE.0 spike — dev-only; the component self-guards on import.meta.env.DEV. */}
                  <Route path="/dev/mapengine-spike" element={<MapEngineSpike />} />
                </Routes>
                </Suspense>
                <Suspense fallback={null}><ChatPanel /></Suspense>
                </PolygonSyncDialogProvider>
                </KmlDialogProvider>
              </BrowserRouter>
            </MUIThemeWrapper>
          </AuthInitializer>
        </AuthProvider>
      </QueryClientProvider>
    </CustomThemeProvider>
  </StrictMode>,
)

// Cold-boot splash (#ds-splash) is now hidden by AuthProvider's `ready` effect
// (see src/auth/AuthProvider.tsx) — keeping it visible until auth resolves
// prevents the blank-screen flash on refresh of protected routes (UX1 DL.8).
// A fallback safety net hides the splash if AuthProvider never reaches ready
// (e.g. uncaught render error before AuthProvider mounts).
window.setTimeout(() => {
  const splash = document.getElementById('ds-splash');
  if (splash && !splash.classList.contains('hidden')) {
    splash.classList.add('hidden');
    window.setTimeout(() => splash.remove(), 350);
  }
}, 5000);
